import { inject, Injectable, OnDestroy } from '@angular/core';
import { CredentialsCurrentUser, CredentialsService } from '@app/core';
import {
    Cart,
    CreateCartInput,
    ListFiles,
    ProductInOrder,
    ProductInOrderInput,
    Producto,
    UpdateCartInput
} from '@app/core/services/api/API.service';
import {
    AmplifyApiService,
    CartService as CartApiService
} from '@app/core/api/services';
import * as subscriptions from '@app/core/graphql/subscriptions';
import { ROLES_GROUPS } from '@app/constants';
import {
    filterPremiumCartBrandingFiles,
    generatePremiumCartFileName
} from '@app/modules/shopping-cart/premiums/premiums.service';
import {
    getFileNameByUUID,
    removeDuplicates,
    removeTypename
} from '@app/shared/services/util.service';
import { BrandfactoryServerlessService } from '@app/shared/services/serverless/brandfactory-serverless.service';
import { StorageService } from '@app/shared/services/storage.service';
import { cloneDeep } from 'lodash';
import {
    BehaviorSubject,
    catchError,
    filter,
    forkJoin,
    from,
    map,
    Observable,
    of,
    Subscription,
    switchMap,
    take,
    tap,
    throwError
} from 'rxjs';

@Injectable({
    providedIn: 'root'
})
export class PremiumSessionCartService implements OnDestroy {
    private _cartApiService = inject(CartApiService);
    private _amplifyApiService = inject(AmplifyApiService);
    private _storageService = inject(StorageService);
    private _credentialsService = inject(CredentialsService);
    private _brandfactoryServerlessService = inject(
        BrandfactoryServerlessService
    );

    private _cart$ = new BehaviorSubject<Cart | null>(null);
    private _cartProducts$ = new BehaviorSubject<Producto[] | null>(null);
    private _cartProduct$ = new BehaviorSubject<ProductInOrder | null>(null);
    private _product$ = new BehaviorSubject<Producto | null>(null);

    private _sessionScope: string | null = null;
    private _hasLoaded = false;
    /** updatedAt del carrito aplicado en memoria para detectar cambios remotos (p. ej. agente). */
    private _lastSyncedUpdatedAt: string | null = null;
    /** updatedAt del carrito cuyos productos ya están hidratados en cartProducts$. */
    private _hydratedForCartUpdatedAt: string | null = null;
    private _cartUpdateSubscription: Subscription | null = null;
    private _cartSubscriptionScope: string | null = null;

    get cart$(): Observable<Cart | null> {
        return this._cart$.asObservable();
    }

    get cartProducts$(): Observable<Producto[] | null> {
        return this._cartProducts$.asObservable();
    }

    get cartProduct$(): Observable<ProductInOrder | null> {
        return this._cartProduct$.asObservable();
    }

    get product$(): Observable<Producto | null> {
        return this._product$.asObservable();
    }

    getCartSnapshot(): Cart | null {
        return this._cart$.getValue();
    }

    getLastSyncedUpdatedAt(): string | null {
        return this._lastSyncedUpdatedAt;
    }

    isMktUser(user: CredentialsCurrentUser | null): boolean {
        return !!user?.id && ROLES_GROUPS.MKT_GROUP.includes(user.role);
    }

    resolveCartId(user: CredentialsCurrentUser): string | null {
        return user.isCoupa ? user.buyerCookie : user.id;
    }

    /** Coupa carts use buyerCookie as both id and creator. */
    resolveCartCreator(user: CredentialsCurrentUser): string | null {
        return user.isCoupa ? user.buyerCookie : user.id;
    }

    isCartOwnedByUser(cart: Cart, user: CredentialsCurrentUser): boolean {
        const cartId = this.resolveCartId(user);
        if (!cartId || cart.id !== cartId) {
            return false;
        }
        const expectedCreator = this.resolveCartCreator(user);
        return cart.creator === expectedCreator || cart.creator === user.id;
    }

    isLoadedForUser(user: CredentialsCurrentUser | null): boolean {
        if (!this.isMktUser(user)) {
            return false;
        }
        return (
            this._hasLoaded && this._sessionScope === this.buildScopeKey(user)
        );
    }

    hasRemoteUpdates(cart: Cart): boolean {
        if (!this._lastSyncedUpdatedAt || !cart?.updatedAt) {
            return false;
        }
        return cart.updatedAt !== this._lastSyncedUpdatedAt;
    }

    async loadCart(
        user: CredentialsCurrentUser | null,
        forceRefresh = false
    ): Promise<Cart | null> {
        if (!this.isMktUser(user)) {
            this.clear();
            return null;
        }

        const scopeKey = this.buildScopeKey(user);
        if (this._sessionScope && this._sessionScope !== scopeKey) {
            this.resetSessionState();
        }

        if (
            !forceRefresh &&
            this._hasLoaded &&
            this._sessionScope === scopeKey
        ) {
            this.ensureCartUpdateSubscription(user);
            return this._cart$.getValue();
        }

        return this.fetchAndApplyCart(user);
    }

    /** Refresca el carrito desde API solo si hace falta (p. ej. cambios remotos de agente). */
    async refreshFromServer(
        user: CredentialsCurrentUser | null
    ): Promise<Cart | null> {
        if (!this.isMktUser(user)) {
            return null;
        }
        return this.fetchAndApplyCart(user);
    }

    /** Carga el carrito e hidrata productos (Observable para resolvers y componentes). */
    loadCart$(_cartId?: string, forceRefresh = false): Observable<Cart | null> {
        const user = this._credentialsService.credentialsCurrentUser;
        return from(this.loadCart(user, forceRefresh)).pipe(
            switchMap(cart => {
                if (
                    !forceRefresh &&
                    cart &&
                    this.shouldSkipProductHydration(cart)
                ) {
                    return of(cart);
                }
                return this.hydrateCartProducts$(cart);
            })
        );
    }

    getProductInCartByIndex(index: number): Observable<ProductInOrder | null> {
        if (!Number.isInteger(index) || index < 0) {
            return of(null);
        }

        return this.cart$.pipe(
            filter((cart): cart is Cart => cart != null),
            take(1),
            map(cart => {
                if (cart.products?.length > index) {
                    return cart.products[index];
                }
                console.error('Índice de producto en carrito fuera de rango');
                return null;
            }),
            tap(cartProduct => {
                if (cartProduct) {
                    this._cartProduct$.next(cartProduct);
                }
            }),
            catchError(error => {
                console.error(
                    'Error al acceder al producto en el carrito:',
                    error
                );
                return of(null);
            })
        );
    }

    getProductById(id: string): Observable<Producto> {
        return from(
            this._brandfactoryServerlessService.getProductsDetailById(id)
        ).pipe(
            take(1),
            map(data => {
                this._product$.next(data);
                return data;
            }),
            switchMap(product => {
                if (!product) {
                    return throwError(
                        () =>
                            new Error(
                                'Could not found product with id of ' + id + '!'
                            )
                    );
                }
                return of(product);
            })
        );
    }

    async ensureCartFromServer(
        user: CredentialsCurrentUser | null
    ): Promise<Cart | null> {
        if (!this.isMktUser(user)) {
            return null;
        }
        return this.refreshFromServer(user);
    }

    async saveProductInCart(
        product: ProductInOrderInput,
        user?: CredentialsCurrentUser | null
    ): Promise<Cart | null> {
        const resolvedUser =
            user ?? this._credentialsService.credentialsCurrentUser;
        if (!this.isMktUser(resolvedUser)) {
            return null;
        }

        const cartId = this.resolveCartId(resolvedUser);
        if (!cartId) {
            return null;
        }

        let cart = await this.ensureCartKnown(resolvedUser);

        if (cart) {
            const products = removeTypename([
                ...(cart.products ?? []),
                product
            ]);
            return this.updateCart({ id: cart.id, products }).toPromise();
        }

        const creator = this.resolveCartCreator(resolvedUser);
        if (!creator) {
            return null;
        }

        const createCartInput: CreateCartInput = {
            id: cartId,
            creator,
            products: [product]
        };
        const created = await this.createCart(createCartInput).toPromise();
        if (created) {
            return created;
        }

        const existingCart = await this._cartApiService.getCart(cartId);
        if (
            !existingCart ||
            !this.isCartOwnedByUser(existingCart, resolvedUser)
        ) {
            return null;
        }

        const products = removeTypename([
            ...(existingCart.products ?? []),
            product
        ]);
        return this.updateCart({ id: existingCart.id, products }).toPromise();
    }

    updateCart(updateCartInput: UpdateCartInput): Observable<Cart> {
        const user = this._credentialsService.credentialsCurrentUser;
        if (!this.isMktUser(user)) {
            return throwError(() => new Error('Cart updates are MKT-only'));
        }

        return from(
            this._cartApiService.updateCart(removeTypename(updateCartInput))
        ).pipe(
            tap(updatedCart => {
                if (updatedCart && this.isCartOwnedByUser(updatedCart, user)) {
                    this.applyCart(user, updatedCart);
                }
            }),
            catchError(error => {
                console.error('Error al actualizar el carrito:', error);
                return throwError(() => error);
            })
        );
    }

    createCart(cartInput: CreateCartInput): Observable<Cart> {
        const user = this._credentialsService.credentialsCurrentUser;
        if (!this.isMktUser(user)) {
            return throwError(() => new Error('Cart creation is MKT-only'));
        }

        return from(
            this._cartApiService.createCart(removeTypename(cartInput))
        ).pipe(
            tap(newCart => {
                if (newCart && this.isCartOwnedByUser(newCart, user)) {
                    this.applyCart(user, newCart);
                }
            }),
            catchError(error => {
                console.error('Error al crear el carrito:', error);
                return throwError(() => error);
            })
        );
    }

    deleteCart(cartId: string): Observable<Cart> {
        const user = this._credentialsService.credentialsCurrentUser;
        if (!this.isMktUser(user)) {
            return throwError(() => new Error('Cart deletion is MKT-only'));
        }

        return from(this._cartApiService.deleteCart({ id: cartId })).pipe(
            tap(() => this.clear()),
            catchError(error => {
                console.error('Error al eliminar el carrito:', error);
                return throwError(() => error);
            })
        );
    }

    async persistBrandingCanvasFile(
        canvasFile: File,
        user?: CredentialsCurrentUser | null
    ): Promise<Cart | null> {
        const resolvedUser =
            user ?? this._credentialsService.credentialsCurrentUser;
        if (!this.isMktUser(resolvedUser)) {
            return null;
        }

        const cartId = this.resolveCartId(resolvedUser);
        if (!cartId) {
            return null;
        }

        let cart = await this.ensureCartKnown(resolvedUser);

        if (!cart) {
            const creator = this.resolveCartCreator(resolvedUser);
            if (!creator) {
                return null;
            }
            cart = await this.createCart({
                id: cartId,
                creator,
                products: []
            }).toPromise();
            if (!cart) {
                cart = await this._cartApiService.getCart(cartId);
            }
        }

        if (!cart) {
            return null;
        }

        const cartFiles = cart.files ? [...cart.files] : [];
        const brandingFiles = filterPremiumCartBrandingFiles(cartFiles);
        const fileIndex = brandingFiles.length;
        const dateFile = new Date().toISOString().slice(0, 10);

        const name = getFileNameByUUID(
            `branding-${fileIndex + 1}`,
            canvasFile.name
        );
        const fileName = generatePremiumCartFileName(
            name,
            'premiums',
            'shopping-cart',
            cartId,
            dateFile,
            fileIndex,
            'branding'
        );

        await this._storageService.uploadFile(fileName, canvasFile);

        const newFile: ListFiles = {
            created: new Date().getTime(),
            creator_id: resolvedUser.id,
            __typename: 'ListFiles',
            file: {
                key: fileName,
                __typename: 'File'
            },
            statusFile: 'uploaded',
            name: `branding-${canvasFile.name}-${fileIndex + 1}`,
            description: `Archivo de branding ${fileIndex + 1}`,
            provider_id: resolvedUser.clientId ? resolvedUser.clientId : null,
            product_id: null
        };

        return this.updateCart({
            id: cartId,
            files: removeTypename([...cartFiles, newFile])
        }).toPromise();
    }

    clear(): void {
        this.teardownCartUpdateSubscription();
        this.resetSessionState();
    }

    ngOnDestroy(): void {
        this.teardownCartUpdateSubscription();
    }

    private hydrateCartProducts$(cart: Cart | null): Observable<Cart | null> {
        if (!cart?.products?.length) {
            this._cartProducts$.next([]);
            this._hydratedForCartUpdatedAt = cart?.updatedAt ?? null;
            return of(cart);
        }

        const productsCart = removeDuplicates(
            cloneDeep(cart.products),
            'product_id'
        );

        return forkJoin(
            productsCart.map(p =>
                this._brandfactoryServerlessService.getProductsDetailById(
                    p.product_id
                )
            )
        ).pipe(
            map(products => {
                this._cartProducts$.next(products);
                this._hydratedForCartUpdatedAt = cart.updatedAt ?? null;
                return cart;
            })
        );
    }

    /** Usa caché de sesión si ya se cargó; solo llama GetCart si aún no hay datos. */
    private async ensureCartKnown(
        user: CredentialsCurrentUser
    ): Promise<Cart | null> {
        if (this.isLoadedForUser(user)) {
            return this.getCartSnapshot();
        }
        return this.fetchAndApplyCart(user);
    }

    private shouldSkipProductHydration(cart: Cart | null): boolean {
        if (!cart?.updatedAt) {
            return false;
        }
        return this._hydratedForCartUpdatedAt === cart.updatedAt;
    }

    private async fetchAndApplyCart(
        user: CredentialsCurrentUser
    ): Promise<Cart | null> {
        const cartId = this.resolveCartId(user);
        if (!cartId) {
            this.resetSessionState();
            return null;
        }

        try {
            const cart = await this._cartApiService.getCart(cartId);
            if (cart && !this.isCartOwnedByUser(cart, user)) {
                this.teardownCartUpdateSubscription();
                this.resetSessionState();
                return null;
            }
            this.ensureCartUpdateSubscription(user);
            this.applyCart(user, cart ?? null);
            return cart ?? null;
        } catch {
            this.applyCart(user, null);
            return null;
        }
    }

    /** Escucha cambios remotos (p. ej. agente) solo del carrito del usuario MKT actual. */
    private ensureCartUpdateSubscription(user: CredentialsCurrentUser): void {
        if (!this.isMktUser(user)) {
            return;
        }

        const scopeKey = this.buildScopeKey(user);
        if (
            this._cartSubscriptionScope === scopeKey &&
            this._cartUpdateSubscription
        ) {
            return;
        }

        this.teardownCartUpdateSubscription();

        const cartId = this.resolveCartId(user);
        if (!cartId) {
            return;
        }

        this._cartSubscriptionScope = scopeKey;
        this._cartUpdateSubscription = this._amplifyApiService
            .subscribe({
                query: subscriptions.onUpdateCart,
                variables: {
                    filter: {
                        id: { eq: cartId }
                    }
                }
            })
            .subscribe({
                next: observer => {
                    const updatedCart = observer?.data?.onUpdateCart as
                        | Cart
                        | null
                        | undefined;
                    if (!updatedCart) {
                        return;
                    }

                    const currentUser = this._credentialsService
                        .credentialsCurrentUser;
                    if (!this.isMktUser(currentUser)) {
                        return;
                    }
                    if (!this.isCartOwnedByUser(updatedCart, currentUser)) {
                        return;
                    }
                    if (
                        this._hasLoaded &&
                        !this.hasRemoteUpdates(updatedCart)
                    ) {
                        return;
                    }

                    this.applyCart(currentUser, updatedCart);
                },
                error: error => {
                    console.error('Error en suscripción onUpdateCart:', error);
                }
            });
    }

    private teardownCartUpdateSubscription(): void {
        this._cartUpdateSubscription?.unsubscribe();
        this._cartUpdateSubscription = null;
        this._cartSubscriptionScope = null;
    }

    private applyCart(user: CredentialsCurrentUser, cart: Cart | null): void {
        this._sessionScope = this.buildScopeKey(user);
        this._hasLoaded = true;
        this._lastSyncedUpdatedAt = cart?.updatedAt ?? null;
        this._cart$.next(cart);

        if (!this.shouldSkipProductHydration(cart)) {
            void this.hydrateCartProducts$(cart).toPromise();
        }
    }

    private resetSessionState(): void {
        this._sessionScope = null;
        this._hasLoaded = false;
        this._lastSyncedUpdatedAt = null;
        this._hydratedForCartUpdatedAt = null;
        this._cart$.next(null);
        this._cartProducts$.next(null);
        this._cartProduct$.next(null);
        this._product$.next(null);
    }

    private buildScopeKey(user: CredentialsCurrentUser): string {
        const cartId = this.resolveCartId(user);
        return `${user.id}:${cartId ?? ''}`;
    }
}
